import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

// How many prefix neighbours one keystroke reads. The agent's own widening
// reads 250 and the server search 75; at 25 a complete value went unfound once
// that many neighbours shared its first three characters (M119).
const PREFIX_READ_LIMIT = 100;

// The identifier column's index (Spec 4 section 3.2): one entry per identifier,
// carrying every prefix of its canonical text from IDENTIFIER_PREFIX_MIN
// characters up, each cut at IDENTIFIER_PREFIX_MAX, as
// functions/common/search_index.py::identifier_search_tokens writes them.
const IDENTIFIER_PREFIX_MIN = 3;
const IDENTIFIER_PREFIX_MAX = 40;

// The index stores each word's lowercase alphanumeric prefixes (backend
// generate_search_tokens), so a typed term is reduced the same way before it
// is matched: "O'Brien" asks for "obr" and "AN-100046-00" for "an1" (M24).
export function canonicalSearchText(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

// The identifier index's token for a typed term: the whole term's canonical
// text as one token ("POL-123" asks for "pol123"), cut at IDENTIFIER_PREFIX_MAX;
// none for a term under IDENTIFIER_PREFIX_MIN letters and digits, which no
// identifier is indexed under.
export function identifierToken(input) {
  const canonical = canonicalSearchText(input);
  if (canonical.length < IDENTIFIER_PREFIX_MIN) return '';
  return canonical.slice(0, IDENTIFIER_PREFIX_MAX);
}

// The token of the word being typed: the last word with any alphanumerics.
export function deriveToken(input) {
  const words = String(input || '').toLowerCase().trim().split(/\s+/);
  for (let index = words.length - 1; index >= 0; index -= 1) {
    const cleaned = canonicalSearchText(words[index]);
    if (cleaned) return cleaned.slice(0, 3);
  }
  return '';
}

// An index entry's id is the SHA-1 of its canonical search text (backend
// compute_value_id): every spelling that reduces to the same letters and digits
// ("ACME LTD", "Acme Ltd.") shares one entry, which lists each of them under
// `spellings`. A complete term is therefore read directly by its id, however
// many neighbours share its prefix (M119).
export async function valueIdOf(term) {
  const bytes = new TextEncoder().encode(canonicalSearchText(term));
  const digest = await crypto.subtle.digest('SHA-1', bytes);
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

// An identifier's index entry id is the SHA-1 of the identifier's exact text
// (backend compute_identifier_entry_id). Unlike valueIdOf nothing is reduced,
// because the main table keys its rows by the exact string: POL-123 and
// pol-123 are two policies, with two entries.
export async function identifierEntryIdOf(identifier) {
  const bytes = new TextEncoder().encode(identifier);
  const digest = await crypto.subtle.digest('SHA-1', bytes);
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}


// The suggestions for a filter on an indexed column of the tables `tableId`
// names (one id or a list). Every column is searched the same way: the entries
// carrying the typed text's token, at most PREFIX_READ_LIMIT of them, and the
// entry the typed text spells out in full, read directly by its id. The
// identifier column (`isIdentifier`, Spec 6 sections 6 and 9) differs in three
// things only: its token is the typed text's whole canonical form, its entry
// id is the exact identifier's, and an identifier is offered when the typed
// text's canonical form is a prefix of its own, the exact identifier included
// ("POL-123" offers POL123 too: the filter itself stays exact).
export function useIndexedValueSearch(tableId, columnName, { isIdentifier = false } = {}) {
  const { companyName } = useAuth();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  // Every search() call starts a new generation. A read that finishes after a
  // newer call (a newer term, a cleared input, another table or column) is
  // stale and must not write options or loading.
  const generationRef = useRef(0);

  // Keyed by content, not array identity, so a caller passing a fresh array
  // each render does not restart the search.
  const tableKey = JSON.stringify(
    Array.isArray(tableId) ? tableId.filter(Boolean) : (tableId ? [tableId] : [])
  );

  // Keeps the existing empty array when there is nothing to clear, so the
  // routine clears on mount and on an emptied input cost no re-render.
  const clearResults = () => {
    setOptions((prev) => (prev.length ? [] : prev));
    setLoading(false);
  };

  // A different table or column: what is shown belongs to the old scope. The
  // cleanup also invalidates a pending or in-flight read on unmount.
  useEffect(() => {
    clearResults();
    return () => {
      generationRef.current += 1;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [companyName, tableKey, columnName, isIdentifier]);

  const search = useCallback((term) => {
    const generation = ++generationRef.current;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const tableIds = JSON.parse(tableKey);
    const token = isIdentifier ? identifierToken(term) : deriveToken(term);
    if (!companyName || tableIds.length === 0 || !columnName || !token) {
      clearResults();
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // The entry the typed text spells out in full is found however many
        // neighbours share its token (M119): an identifier's by its exact text
        // with only the surrounding whitespace trimmed, as the backend's
        // find_identifiers reads it; any other value's by its canonical text.
        const exactId = isIdentifier ? await identifierEntryIdOf(term.trim()) : await valueIdOf(term);
        const queryPromises = tableIds.map(async (tid) => {
          const valuesRef = collection(db, `users/${companyName}/tables/${tid}/indexes/${columnName}/values`);
          const [prefixSnap, exactSnap] = await Promise.all([
            getDocs(query(valuesRef, where('search_tokens', 'array-contains', token), limit(PREFIX_READ_LIMIT))),
            getDoc(doc(valuesRef, exactId)),
          ]);
          const entries = prefixSnap.docs.map(d => d.data());
          if (exactSnap.exists()) entries.push(exactSnap.data());
          return entries;
        });
        const entriesByTable = await Promise.all(queryPromises);
        // Every stored spelling is its own option: two uploads that spelt one
        // insured "ACME LTD" and "Acme Ltd" are both offered, because the rows
        // are stored under both. An identifier's entry has one spelling, the
        // identifier itself.
        const spellings = entriesByTable.flat().flatMap(entry => entry.spellings);
        const wanted = canonicalSearchText(term);
        // An identifier is offered when its canonical form starts with the typed
        // text's: a term longer than IDENTIFIER_PREFIX_MAX shares its token with
        // identifiers it is not a prefix of. Any other spelling is offered when
        // its canonical form contains the typed text's.
        const matching = isIdentifier
          ? spellings.filter(spelling => canonicalSearchText(spelling).startsWith(wanted))
          : spellings.filter(spelling => canonicalSearchText(spelling).includes(wanted));
        const filtered = Array.from(new Set(matching))
          .sort((a, b) => a.localeCompare(b))
          .map(spelling => ({ value: spelling }));
        if (generation !== generationRef.current) return;
        setOptions(filtered);
      } catch (e) {
        console.error('Indexed search failed:', e);
        if (generation !== generationRef.current) return;
        setOptions([]);
      } finally {
        if (generation === generationRef.current) setLoading(false);
      }
    }, 300);
  }, [companyName, tableKey, columnName, isIdentifier]);

  return useMemo(() => ({ search, options, loading }), [search, options, loading]);
}
